/**
 * tableViewConfig.ts
 *
 * Tier 1 display config for the dashboard **Table** widget (#969).
 *
 * ## The models, after Matt's #969 Obs 12-15 review
 *
 * Row/column DATA selection now uses the same positional allowlist as the
 * chart (`utils/tableSelection.ts`, stored on `selection`), driven by the
 * flat Select Data dropdowns — the nested blocklist checkbox tree is gone.
 * `view_config` remains the table-mode home for what a selection cannot
 * carry: `column_widths`, `freeze_headers`, and the two group-label toggles
 * `show_row_group_labels` / `show_column_group_labels` (#969 comment 2 item 4,
 * which split Obs 15's single `hide_structural_labels`). Its LEGACY
 * `hidden_rows`/`hidden_columns`/`row_order`/`column_order` blocklist is still
 * read and applied for widgets saved under the old model, until the first
 * Select Data edit retires it.
 *
 * This module also owns the table-mode display transforms: the
 * group-preserving positional filter (`applySelectionToTableData`), the
 * Data Type band SET (`applyTableDataType` — a table can draw several bands
 * at once, #969 comment 1 item 2), the Switch Axis transpose
 * (`transposePlatinumData`, no longer offered as a table control but still
 * honoured where stored), and the two group-label strips
 * (`stripRowGroupLabels` / `stripColumnGroupLabels`). It also decides what the
 * Select Data dropdowns list (`axisLeafNodes`). All DISPLAY level —
 * the source table, its spec, and Carbon settings are never touched.
 *
 * ## Identity: a path, not a label
 *
 * A CBT side axis is a tree (Total, then Gender → Male/Female → age bands),
 * and the same leaf label appears under several parents. So a node is
 * identified by its **path from the root**, joined with a unit separator, e.g.
 * `Gender \u001F Male \u001F 18-24`. Two consequences fall out for free:
 *
 * - Hiding a parent hides its children — a child's id is prefixed by its
 *   parent's id, so one prefix test covers the subtree (#969: "collapsing or
 *   hiding a parent hides all its children").
 * - An orphaned hide reference is one that matches no node in the current
 *   tree. #969 Part D says clean those silently; `resolveHiddenIds` returns
 *   them separately so the Review Changes panel can log what it dropped.
 *
 * ## Where it is applied
 *
 * At the data-model seam, like the chart's selection: the config is applied to
 * `PlatinumData` before the renderer sees it, so the PPTX export
 * (`utils/pptxExport.ts`, which reads the filtered data, never the DOM) and
 * the graph half of the widget inherit it without knowing it exists.
 *
 * The hard part is not dropping vecs — it is that side and top GROUPS carry
 * absolute grid coordinates (`x/y/w/h`) computed by Carbon over a table where
 * nothing is missing. Remove a subtree and every surviving group's span is
 * wrong. `applyViewConfigToPlatinumData` recomputes them; see
 * `recomputeSideGroups` / `recomputeTopGroups`.
 */

import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { PlatinumGroup } from '../types/carbon/models/PlatinumGroup'
import { PlatinumVec } from '../types/carbon/models/PlatinumVec'
import { TableViewConfig } from '../types/gotypes'
import {
  computeHeaderCols,
  computeHeaderRows,
  sideVecBandCounts,
} from '../components/platinum-table-new/layout'
import { resolveRowColumnFilters } from './tableSelection'

/**
 * Separator between path segments in a node id. A unit separator, because it
 * cannot occur in a Carbon label — `/` and `>` both can.
 */
export const NODE_PATH_SEP = '\u001F'

export function joinNodePath(path: string[]): string {
  return path.join(NODE_PATH_SEP)
}

export function splitNodePath(id: string): string[] {
  return id.split(NODE_PATH_SEP)
}

/** One node of a side or top hierarchy: a group, or a vec at the leaf. */
export interface AxisNode {
  /** Path from the root, joined with NODE_PATH_SEP. Stable across refreshes. */
  id: string
  path: string[]
  label: string
  /** Depth in the tree, 0 for a top-level node. */
  depth: number
  children: AxisNode[]
  /** Set on leaves only: index into `side.vecs` / `top.vecs`. */
  vecIndex?: number
}

/** A group rectangle plus the vec range it covers, in vec-index space. */
interface GroupExtent {
  group: PlatinumGroup
  /** Inclusive-exclusive vec index range. */
  start: number
  end: number
  depth: number
}

// ============================================================================
// TREE CONSTRUCTION
// ============================================================================

/**
 * Maps each side group to the range of side vecs it covers.
 *
 * Side group `y`/`h` are absolute GRID rows, and one vec can render as several
 * band rows, so the conversion runs through the same band counts the renderer
 * uses (`sideVecBandCounts`). Anything else drifts the moment column percents
 * are switched on.
 */
function sideGroupExtents(data: PlatinumData): GroupExtent[] {
  const bands = sideVecBandCounts(data)
  const headerRows = computeHeaderRows(data)

  // Grid row at which each vec starts, and the vec that owns each band row.
  const vecOfBandRow: number[] = []
  bands.forEach((count, vecIndex) => {
    for (let b = 0; b < count; b++) vecOfBandRow.push(vecIndex)
  })

  const extents: GroupExtent[] = []
  for (const group of data.side?.groups || []) {
    const firstBandRow = (group.y ?? 0) - headerRows
    const lastBandRow = firstBandRow + (group.h ?? 1) - 1
    const start = vecOfBandRow[firstBandRow]
    const end = vecOfBandRow[lastBandRow]
    if (start === undefined || end === undefined) continue
    extents.push({ group, start, end: end + 1, depth: group.x ?? 0 })
  }
  return extents
}

/**
 * The innermost side-group heading over each side vec (#1808), or undefined
 * for a vec no group covers (a leading base row, or a table with no groups).
 * "Innermost" is the covering group with the greatest depth, so a statement
 * nested under a section heading names the statement. Index-aligned with
 * `side.vecs`.
 */
export function innermostSideGroupLabels(data: PlatinumData): (string | undefined)[] {
  const vecCount = data.side?.vecs?.length ?? 0
  const labels: (string | undefined)[] = new Array(vecCount).fill(undefined)
  const depths: number[] = new Array(vecCount).fill(-1)
  for (const { group, start, end, depth } of sideGroupExtents(data)) {
    const text = group.text?.trim()
    if (!text) continue
    for (let i = start; i < end && i < vecCount; i++) {
      if (depth > depths[i]) {
        depths[i] = depth
        labels[i] = text
      }
    }
  }
  return labels
}

/**
 * Maps each top group to the range of top vecs it covers.
 *
 * Top group `x`/`w` are absolute grid columns; one top vec is exactly one
 * column, so the only conversion is the side-header offset.
 */
function topGroupExtents(data: PlatinumData): GroupExtent[] {
  const headerCols = computeHeaderCols(data)
  const vecCount = data.top?.vecs?.length ?? 0

  const extents: GroupExtent[] = []
  for (const group of data.top?.groups || []) {
    const start = (group.x ?? 0) - headerCols
    const end = start + (group.w ?? 1)
    if (end <= 0 || start >= vecCount) continue
    extents.push({
      group,
      start: Math.max(0, start),
      end: Math.min(vecCount, end),
      depth: group.y ?? 0,
    })
  }
  return extents
}

/** Appends `#2`, `#3`… to labels that repeat within one parent. */
function disambiguate(labels: string[]): string[] {
  const seen = new Map<string, number>()
  return labels.map(label => {
    const n = (seen.get(label) ?? 0) + 1
    seen.set(label, n)
    return n === 1 ? label : `${label}#${n}`
  })
}

/**
 * Builds one axis hierarchy from its groups and vecs.
 *
 * Nesting is inferred from containment: a group whose vec range sits inside
 * another's, at a greater depth, is its child. Vecs no group covers (a leading
 * "Total" base row is the usual case) sit at the top level, in vec order.
 */
function buildTree(
  extents: GroupExtent[],
  vecs: PlatinumVec[],
): AxisNode[] {
  const sorted = [...extents].sort((a, b) =>
    a.start - b.start || a.depth - b.depth || b.end - a.end)

  // A group's parent is the deepest group that strictly contains it.
  const parentOf = new Map<GroupExtent, GroupExtent | null>()
  for (const child of sorted) {
    let best: GroupExtent | null = null
    for (const candidate of sorted) {
      if (candidate === child) continue
      if (candidate.depth >= child.depth) continue
      if (candidate.start > child.start || candidate.end < child.end) continue
      if (!best || candidate.depth > best.depth) best = candidate
    }
    parentOf.set(child, best)
  }

  // The deepest group covering each vec owns it as a leaf.
  const ownerOf = new Map<number, GroupExtent | null>()
  for (let i = 0; i < vecs.length; i++) {
    let best: GroupExtent | null = null
    for (const candidate of sorted) {
      if (candidate.start > i || candidate.end <= i) continue
      if (!best || candidate.depth > best.depth) best = candidate
    }
    ownerOf.set(i, best)
  }

  const childGroups = (parent: GroupExtent | null) =>
    sorted.filter(e => parentOf.get(e) === parent)
  const childVecs = (parent: GroupExtent | null) =>
    vecs.map((_, i) => i).filter(i => ownerOf.get(i) === parent)

  const build = (parent: GroupExtent | null, parentPath: string[]): AxisNode[] => {
    const groups = childGroups(parent)
    const leaves = childVecs(parent)

    // Siblings render in table order regardless of kind, so order by position.
    const entries: Array<{ start: number; group?: GroupExtent; vecIndex?: number }> = [
      ...groups.map(g => ({ start: g.start, group: g })),
      ...leaves.map(i => ({ start: i, vecIndex: i })),
    ].sort((a, b) => a.start - b.start)

    const labels = disambiguate(entries.map(entry =>
      entry.group ? (entry.group.group.text || '') : (vecs[entry.vecIndex!]?.label || '')))

    return entries.map((entry, i) => {
      const path = [...parentPath, labels[i]]
      if (entry.group) {
        return {
          id: joinNodePath(path),
          path,
          label: labels[i],
          depth: parentPath.length,
          children: build(entry.group, path),
        }
      }
      return {
        id: joinNodePath(path),
        path,
        label: labels[i],
        depth: parentPath.length,
        children: [],
        vecIndex: entry.vecIndex,
      }
    })
  }

  return build(null, [])
}

/** The side (row) hierarchy of a table, as a tree of togglable nodes. */
export function buildSideTree(data: PlatinumData): AxisNode[] {
  return buildTree(sideGroupExtents(data), data.side?.vecs || [])
}

/** The top (column) hierarchy of a table, as a tree of togglable nodes. */
export function buildTopTree(data: PlatinumData): AxisNode[] {
  return buildTree(topGroupExtents(data), data.top?.vecs || [])
}

/**
 * The nodes a Select Data dropdown offers for one axis (#969 comment 2 items
 * 1 and 2): every LEAF, in vec order — one entry per row (or column) the table
 * can draw.
 *
 * This replaced a rule that picked a hierarchy LEVEL and listed that. The level
 * rule failed both of Matt's requirements at once. On his nested test table
 * every row sat under a single "Gender" group, so the top level was one useless
 * entry; it descended past that, but on the real Sky exec table it stopped at
 * the ten brand groups, because descending to the twenty leaves overshot its
 * ~10-entry target. Each of those groups holds a "Cases WF" base row plus its
 * brand row, so the base rows the table actually draws were absent from the
 * control that is supposed to govern them — neither addable nor removable.
 *
 * Leaves are the only listing that satisfies both, and they are what the
 * positional selection contract already assumes: a dropdown entry's index is
 * the vec position it toggles. A group entry never was one row, so listing
 * groups always meant one checkbox standing for several vecs.
 *
 * The count is therefore the axis's row count, not a target — a 20-row table
 * lists 20 entries. Truncating would hide data, which is worse than a long
 * menu; labelling is what keeps a long list readable (see `optionLabels` in
 * TableSelectDataPanel).
 */
export function axisLeafNodes(tree: AxisNode[]): AxisNode[] {
  return flattenTree(tree)
    .filter(node => node.vecIndex !== undefined)
    .sort((a, b) => a.vecIndex! - b.vecIndex!)
}

/** Every node in a tree, parents before children. */
export function flattenTree(nodes: AxisNode[]): AxisNode[] {
  const out: AxisNode[] = []
  const walk = (list: AxisNode[]) => {
    for (const node of list) {
      out.push(node)
      walk(node.children)
    }
  }
  walk(nodes)
  return out
}

// ============================================================================
// RESOLVING THE BLOCKLIST
// ============================================================================

/** True when `id` is the hidden node itself or sits underneath it. */
export function isHiddenByPrefix(id: string, hidden: readonly string[]): boolean {
  return hidden.some(h => id === h || id.startsWith(h + NODE_PATH_SEP))
}

export interface ResolvedHidden {
  /** Hide instructions that still match a node in this table. */
  live: string[]
  /**
   * Hide instructions naming nodes the table no longer has. #969 Part D:
   * cleaned silently — hiding something that does not exist is moot — but
   * reported so the Review Changes panel can log the cleanup.
   */
  orphans: string[]
}

/**
 * Splits a hide list into what still applies and what has gone stale.
 *
 * A reference survives if it names a node, OR if it names an ancestor path of
 * one: hiding `Gender` stays meaningful even though `Gender` is a group, and
 * stays meaningful if only some of its children remain.
 */
export function resolveHiddenIds(
  tree: AxisNode[],
  hidden: readonly string[] | undefined,
): ResolvedHidden {
  if (!hidden || hidden.length === 0) return { live: [], orphans: [] }
  const ids = new Set(flattenTree(tree).map(n => n.id))
  const live: string[] = []
  const orphans: string[] = []
  for (const ref of hidden) {
    if (ids.has(ref)) live.push(ref)
    else orphans.push(ref)
  }
  return { live, orphans }
}

/**
 * Returns the config with dead hide/order/width references dropped.
 *
 * Called by the editor after the table loads, so a saved widget quietly
 * repairs itself rather than carrying references to rows that a wave refresh
 * removed. Returns the SAME object when nothing is stale, so callers can use
 * identity to decide whether a save is needed.
 */
export function cleanOrphanedReferences(
  config: TableViewConfig | undefined,
  data: PlatinumData | undefined,
): { config: TableViewConfig | undefined; orphans: string[] } {
  if (!config || !data) return { config, orphans: [] }

  const rowIds = new Set(flattenTree(buildSideTree(data)).map(n => n.id))
  const colIds = new Set(flattenTree(buildTopTree(data)).map(n => n.id))

  const orphans: string[] = []
  const keep = (ids: Set<string>) => (ref: string) => {
    if (ids.has(ref)) return true
    orphans.push(ref)
    return false
  }
  // Order and width references are dropped silently: unlike a hide, a stale
  // position or width is not something a user would want reported.
  const quietKeep = (ids: Set<string>) => (ref: string) => ids.has(ref)

  const next: TableViewConfig = {
    ...config,
    hidden_rows: (config.hidden_rows || []).filter(keep(rowIds)),
    hidden_columns: (config.hidden_columns || []).filter(keep(colIds)),
    row_order: (config.row_order || []).filter(quietKeep(rowIds)),
    column_order: (config.column_order || []).filter(quietKeep(colIds)),
  }
  if (config.column_widths) {
    next.column_widths = Object.fromEntries(
      Object.entries(config.column_widths).filter(([id]) => colIds.has(id)))
  }

  const unchanged =
    next.hidden_rows!.length === (config.hidden_rows || []).length &&
    next.hidden_columns!.length === (config.hidden_columns || []).length &&
    next.row_order!.length === (config.row_order || []).length &&
    next.column_order!.length === (config.column_order || []).length &&
    Object.keys(next.column_widths || {}).length === Object.keys(config.column_widths || {}).length

  if (unchanged) return { config, orphans: [] }

  // Drop emptied arrays so a repaired config does not carry empty noise.
  for (const key of ['hidden_rows', 'hidden_columns', 'row_order', 'column_order'] as const) {
    if (next[key] && next[key]!.length === 0) delete next[key]
  }
  if (next.column_widths && Object.keys(next.column_widths).length === 0) {
    delete next.column_widths
  }
  return { config: next, orphans }
}

// ============================================================================
// APPLYING THE CONFIG TO THE DATA
// ============================================================================

/**
 * The vec indices an axis keeps, in display order.
 *
 * Reorder is applied WITHIN each set of siblings, never across the tree. That
 * is not a simplification for its own sake: it is what keeps every group's
 * rows contiguous, which is the only reason a group's span can be recomputed
 * as a single rectangle afterwards.
 */
export function resolveAxisOrder(
  tree: AxisNode[],
  hidden: readonly string[] | undefined,
  order: readonly string[] | undefined,
): number[] {
  const hideList = hidden || []
  const out: number[] = []
  const walk = (nodes: AxisNode[]) => {
    for (const node of nodes) {
      if (isHiddenByPrefix(node.id, hideList)) continue
      if (node.vecIndex !== undefined) out.push(node.vecIndex)
      walk(node.children)
    }
  }
  walk(sortTreeByOrder(tree, order))
  return out
}

/** Orders one set of siblings by a saved order; unlisted nodes hold their place. */
function sortSiblings(nodes: AxisNode[], order: readonly string[] | undefined): AxisNode[] {
  if (!order || order.length === 0) return nodes
  const rank = new Map(order.map((id, i) => [id, i]))
  // Nodes the order does not mention keep their table position, after any that
  // it does — so a NEW row from a refresh appears rather than vanishing into
  // an ordering it was never listed in.
  return nodes
    .map((node, i) => ({ node, i, rank: rank.get(node.id) }))
    .sort((a, b) => {
      if (a.rank === undefined && b.rank === undefined) return a.i - b.i
      if (a.rank === undefined) return 1
      if (b.rank === undefined) return -1
      return a.rank - b.rank
    })
    .map(entry => entry.node)
}

/**
 * The tree in display order — what the editor lists and what the renderer
 * gets, so "move up" moves the row in both.
 */
export function sortTreeByOrder(
  nodes: AxisNode[],
  order: readonly string[] | undefined,
): AxisNode[] {
  if (!order || order.length === 0) return nodes
  return sortSiblings(nodes, order).map(node =>
    node.children.length === 0
      ? node
      : { ...node, children: sortTreeByOrder(node.children, order) })
}

/**
 * Rebuilds side groups against a kept, reordered set of vecs.
 *
 * Every surviving group is re-laid over the NEW band geometry: `y` moves to
 * wherever its first kept vec now starts, `h` becomes the band count of the
 * vecs it still has. Groups that lost every vec are dropped, which is what
 * makes hiding a parent look like the branch was never there.
 *
 * `x`/`w` are untouched — hiding rows cannot change how many group columns
 * the side header is nested to. (A column left with no groups at all is then
 * dropped by `identifyEmptyGroupColumns` inside the renderer.)
 */
function recomputeSideGroups(
  data: PlatinumData,
  keptVecs: number[],
  newHeaderRows: number,
  bandsOverride?: number[],
): PlatinumGroup[] {
  const extents = sideGroupExtents(data)
  if (extents.length === 0) return []

  const bands = bandsOverride ?? sideVecBandCounts(data)
  // New band-row start for each kept vec, in display order.
  const newStart = new Map<number, number>()
  let cursor = 0
  for (const vecIndex of keptVecs) {
    newStart.set(vecIndex, cursor)
    cursor += bands[vecIndex] ?? 1
  }

  const out: PlatinumGroup[] = []
  for (const extent of extents) {
    const members = keptVecs.filter(i => i >= extent.start && i < extent.end)
    if (members.length === 0) continue
    const starts = members.map(i => newStart.get(i)!)
    const first = Math.min(...starts)
    const height = members.reduce((sum, i) => sum + (bands[i] ?? 1), 0)
    out.push({
      ...extent.group,
      y: newHeaderRows + first,
      h: height,
    })
  }
  return out
}

/** The top-axis mirror of `recomputeSideGroups`, in column space. */
function recomputeTopGroups(
  data: PlatinumData,
  keptVecs: number[],
  newHeaderCols: number,
): PlatinumGroup[] {
  const extents = topGroupExtents(data)
  if (extents.length === 0) return []

  const newIndex = new Map(keptVecs.map((vecIndex, i) => [vecIndex, i]))

  const out: PlatinumGroup[] = []
  for (const extent of extents) {
    const members = keptVecs.filter(i => i >= extent.start && i < extent.end)
    if (members.length === 0) continue
    const first = Math.min(...members.map(i => newIndex.get(i)!))
    out.push({
      ...extent.group,
      x: newHeaderCols + first,
      w: members.length,
    })
  }
  return out
}

/** Side header width in server coordinates, from a group set. */
function sideThicknessOf(groups: PlatinumGroup[]): number {
  return groups.length > 0 ? Math.max(...groups.map(g => g.x ?? 0)) + 1 : 0
}

/** Top header depth in grid rows, from a group set. */
function topThicknessOf(groups: PlatinumGroup[]): number {
  return groups.length > 0 ? Math.max(...groups.map(g => g.y ?? 0)) + 1 : 0
}

/**
 * Applies a table widget's view config to the data, at the data-model seam.
 *
 * Rows and columns not hidden survive — including ones added since the config
 * was saved, which is the whole point of the blocklist. Groups are recomputed
 * rather than cleared (as the chart's `applySelectionToPlatinumData` clears
 * them), because a table without its side hierarchy is not the same table.
 *
 * Hiding EVERY row, or every column, returns the data untouched: an empty grid
 * reads as a broken widget, and the editor has better ways to say "nothing".
 */
export function applyViewConfigToPlatinumData(
  data: PlatinumData,
  config: TableViewConfig | undefined,
): PlatinumData {
  if (!config) return data

  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []
  if (sideVecs.length === 0 || topVecs.length === 0) return data

  const keptRows = resolveAxisOrder(buildSideTree(data), config.hidden_rows, config.row_order)
  const keptCols = resolveAxisOrder(buildTopTree(data), config.hidden_columns, config.column_order)

  return rebuildTableWithKeptVecs(data, keptRows, keptCols)
}

/**
 * Rebuilds the table around a kept (and possibly reordered) set of vec
 * indices, recomputing side and top group spans so the hierarchy survives —
 * the group-preserving counterpart to the chart's
 * `applySelectionToPlatinumData`, which clears groups instead.
 *
 * Emptying either axis returns the data untouched: an empty grid reads as a
 * broken widget. An identity keep (everything, in table order) also returns
 * the same object, so callers can use identity to detect a no-op.
 */
export function rebuildTableWithKeptVecs(
  data: PlatinumData,
  keptRows: number[],
  keptCols: number[],
): PlatinumData {
  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []
  if (sideVecs.length === 0 || topVecs.length === 0) return data
  if (keptRows.length === 0 || keptCols.length === 0) return data

  const unchanged =
    keptRows.length === sideVecs.length && keptRows.every((v, i) => v === i) &&
    keptCols.length === topVecs.length && keptCols.every((v, i) => v === i)
  if (unchanged) return data

  // Groups are recomputed against the header geometry they will be drawn in.
  // Both axes can move: dropping the deepest top group shortens the header,
  // which shifts every side group up; emptying the last side group column
  // narrows the stub, which shifts every top group left. Compute the new
  // groups in the OLD offsets first, then correct both by the delta.
  const oldHeaderRows = computeHeaderRows(data)
  const oldHeaderCols = computeHeaderCols(data)

  let topGroups = recomputeTopGroups(data, keptCols, oldHeaderCols)
  let sideGroups = recomputeSideGroups(data, keptRows, oldHeaderRows)

  const rowDelta = topThicknessOf(data.top?.groups || []) - topThicknessOf(topGroups)
  const colDelta = sideThicknessOf(data.side?.groups || []) - sideThicknessOf(sideGroups)

  if (rowDelta !== 0) {
    sideGroups = sideGroups.map(g => ({ ...g, y: (g.y ?? 0) - rowDelta }))
  }
  if (colDelta !== 0) {
    topGroups = topGroups.map(g => ({ ...g, x: (g.x ?? 0) - colDelta }))
  }

  // `cells.rows` is indexed by SIDE VEC — one row of cells per vec, bands are
  // fields on the cell — and `row.cell` by top vec.
  const cellRows = data.cells?.rows || []
  const newRows = keptRows.map(rowIndex => {
    const row = cellRows[rowIndex]
    return {
      ...row,
      cell: keptCols.map(colIndex => (row?.cell || [])[colIndex]).filter(Boolean),
    }
  })

  return {
    ...data,
    top: {
      ...data.top,
      groups: topGroups,
      vecs: keptCols.map(i => topVecs[i]),
    },
    side: {
      ...data.side,
      groups: sideGroups,
      vecs: keptRows.map(i => sideVecs[i]),
    },
    cells: {
      ...data.cells,
      rows: newRows,
    },
  }
}

/**
 * Column widths keyed by DATA COLUMN INDEX in the filtered table.
 *
 * The config keys widths by column node id, which survives a refresh; the
 * renderer works in column indices, which do not. This converts between them
 * once, against the data the renderer is about to draw. Returns undefined when
 * no width applies, so the renderer keeps its own defaults.
 */
export function resolveColumnWidths(
  data: PlatinumData | undefined,
  config: TableViewConfig | undefined,
): Record<number, number> | undefined {
  const widths = config?.column_widths
  if (!widths || !data) return undefined

  const leaves = flattenTree(buildTopTree(data)).filter(n => n.vecIndex !== undefined)
  const out: Record<number, number> = {}
  for (const leaf of leaves) {
    const width = widths[leaf.id]
    if (width && width > 0) out[leaf.vecIndex!] = width
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** Freeze defaults ON — a scrolling crosstab is unreadable without it. */
export function isFrozen(config: TableViewConfig | undefined): boolean {
  return config?.freeze_headers ?? true
}

// ============================================================================
// TABLE-MODE SELECT DATA (#969 Obs 12-15) — display-seam transforms
// ============================================================================

/**
 * The vec positions a legacy hide list currently removes from an axis —
 * leaves whose id is the hidden node or sits underneath it. The Select Data
 * panel's display seam for widgets saved under the old blocklist tree: their
 * dropdowns start from "all minus these" until the first positional edit.
 */
export function hiddenLeafPositions(
  tree: AxisNode[],
  hidden: readonly string[] | undefined,
): number[] {
  if (!hidden || hidden.length === 0) return []
  return flattenTree(tree)
    .filter(n => n.vecIndex !== undefined && isHiddenByPrefix(n.id, hidden))
    .map(n => n.vecIndex!)
    .sort((a, b) => a - b)
}

/**
 * Applies a resolved chart-style selection to a TABLE, keeping the axis
 * hierarchy: rows/columns are filtered by position (or legacy name), and the
 * surviving groups are re-laid over the kept vecs rather than cleared. The
 * table-mode counterpart to `applySelectionToPlatinumData` (#969 Obs 13 —
 * the Select Data dropdowns drive table mode too, but a table without its
 * nesting is not the same table).
 */
export function applySelectionToTableData(
  data: PlatinumData,
  resolved: {
    rows: string[]
    columns: string[]
    rowIndices?: number[]
    columnIndices?: number[]
  },
): PlatinumData {
  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []

  const { filterRows, filterColumns, keepRow, keepColumn } = resolveRowColumnFilters(resolved)
  if (!filterRows && !filterColumns) return data

  const keptRows = sideVecs
    .map((vec, index) => ({ vec, index }))
    .filter(({ vec, index }) => !filterRows || keepRow(vec.label || '', index))
    .map(({ index }) => index)
  const keptCols = topVecs
    .map((vec, index) => ({ vec, index }))
    .filter(({ vec, index }) => !filterColumns || keepColumn(vec.label || '', index))
    .map(({ index }) => index)

  // Never hand back an empty table — that reads as broken, not filtered.
  if (keptRows.length === 0 || keptCols.length === 0) return data

  return rebuildTableWithKeptVecs(data, keptRows, keptCols)
}

/**
 * The three cell bands a table can draw, in the order Carbon stacks them.
 */
export const TABLE_DATA_TYPES = ['freq', 'colPct', 'rowPct'] as const
export type TableDataType = typeof TABLE_DATA_TYPES[number]

/**
 * The bands a table widget should render (#969 comment 1 item 2).
 *
 * A table shows Frequencies, Column % and Row % SIMULTANEOUSLY if the user
 * asks for them — which is why the table's Data Type control is a set of
 * checkboxes, not the chart's single-select dropdown. `data_types` is the set;
 * `data_type` is the single value existing widgets already carry, read as a
 * one-element set so they render exactly as before.
 *
 * Always returned in canonical band order, so the stack does not reshuffle
 * with the order the boxes were ticked. An empty result means "no explicit
 * pick": the CBT's own bands stand.
 */
export function resolveTableDataTypes(
  dataTypes: readonly string[] | undefined,
  dataType: string | undefined,
): TableDataType[] {
  const raw = dataTypes && dataTypes.length > 0
    ? dataTypes
    : (dataType ? [dataType] : [])
  return TABLE_DATA_TYPES.filter(band => raw.includes(band))
}

/**
 * Which bands a table widget's Data Type shows (#969 Obs 14, extended to a
 * SET by #969 comment 1 item 2) — DISPLAY level only. The payload already
 * carries every band's values (the graph half reads them by dataType from the
 * same fetch); the table decides which bands to draw from `properties.cells`,
 * so this rewrites those booleans and re-lays the side groups over the new
 * band geometry. An absent, empty, or unrecognized pick leaves the data
 * untouched — existing widgets keep their CBT-default bands, and no pick can
 * produce a table with every band switched off.
 */
export function applyTableDataType(
  data: PlatinumData,
  dataType: string | readonly string[] | undefined,
): PlatinumData {
  const bands = resolveTableDataTypes(
    Array.isArray(dataType) ? dataType : undefined,
    typeof dataType === 'string' ? dataType : undefined,
  )
  if (bands.length === 0) return data
  const props = data.properties
  if (!props?.cells) return data

  const next = {
    frequencies: bands.includes('freq'),
    columnPercents: bands.includes('colPct'),
    rowPercents: bands.includes('rowPct'),
  }
  if (props.cells.frequencies === next.frequencies &&
      props.cells.columnPercents === next.columnPercents &&
      props.cells.rowPercents === next.rowPercents) {
    return data
  }

  const newData: PlatinumData = {
    ...data,
    properties: { ...props, cells: { ...props.cells, ...next } },
  }

  // Side group y/h are in band-row space, and the band count per vec just
  // changed — re-lay every group over the new geometry, same vec ranges.
  const sideGroups = data.side?.groups || []
  if (sideGroups.length > 0) {
    const keptVecs = (data.side?.vecs || []).map((_, i) => i)
    const newBands = sideVecBandCounts(newData)
    newData.side = {
      ...data.side,
      groups: recomputeSideGroups(data, keptVecs, computeHeaderRows(data), newBands),
    }
  }
  return newData
}

/**
 * Transposes a table for display (#969 Obs 13 — the Switch Axis checkbox in
 * table mode): side and top swap, cells transpose, and both axes' groups are
 * re-laid in the new orientation. Display-level only — the source table is
 * untouched.
 */
export function transposePlatinumData(data: PlatinumData): PlatinumData {
  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []
  const cellRows = data.cells?.rows || []
  if (sideVecs.length === 0 || topVecs.length === 0) return data

  // Vec ranges of every group, resolved in the ORIGINAL orientation.
  const oldSideExtents = sideGroupExtents(data)
  const oldTopExtents = topGroupExtents(data)

  // Edge properties follow their axis; significance letters swap orientation.
  const props = data.properties
  const newProps: typeof props = props
    ? {
        ...props,
        side: props.top,
        top: props.side,
        significance: props.significance
          ? {
              ...props.significance,
              letters: props.significance.letters === 'Row'
                ? 'Column'
                : props.significance.letters === 'Column'
                  ? 'Row'
                  : props.significance.letters,
            }
          : props.significance,
      }
    : props

  const newCellRows = topVecs.map((_, i) => ({
    cell: sideVecs.map((__, j) => (cellRows[j]?.cell || [])[i]).filter(Boolean),
  }))

  // Draft without groups: yields the label/letter-only header thicknesses and
  // the band counts of the NEW side vecs under the swapped properties.
  const draft: PlatinumData = {
    ...data,
    properties: newProps,
    meta: data.meta ? { ...data.meta, side: data.meta.top, top: data.meta.side } : data.meta,
    side: { ...data.top, vecs: topVecs, groups: [] },
    top: { ...data.side, vecs: sideVecs, groups: [] },
    cells: { ...data.cells, rows: newCellRows },
  }

  const newTopThickness = oldSideExtents.length > 0
    ? Math.max(...oldSideExtents.map(e => e.depth)) + 1
    : 0
  const newSideThickness = oldTopExtents.length > 0
    ? Math.max(...oldTopExtents.map(e => e.depth)) + 1
    : 0
  const newHeaderRows = newTopThickness + computeHeaderRows(draft)
  const newHeaderCols = newSideThickness + computeHeaderCols(draft)

  // Old top groups become side groups: their vec ranges (old top vec indices)
  // are the new side vecs, laid out in band-row space.
  const newBands = sideVecBandCounts(draft)
  const bandStart: number[] = []
  let cursor = 0
  for (const count of newBands) {
    bandStart.push(cursor)
    cursor += count
  }
  const newSideGroups: PlatinumGroup[] = oldTopExtents.map(extent => {
    const height = newBands.slice(extent.start, extent.end).reduce((a, b) => a + b, 0)
    return {
      ...extent.group,
      x: extent.depth,
      y: newHeaderRows + bandStart[extent.start],
      w: extent.group.h ?? 1,
      h: height,
    }
  })

  // Old side groups become top groups: one column per vec in their range.
  const newTopGroups: PlatinumGroup[] = oldSideExtents.map(extent => ({
    ...extent.group,
    x: newHeaderCols + extent.start,
    y: extent.depth,
    w: extent.end - extent.start,
    h: extent.group.w ?? 1,
  }))

  return {
    ...draft,
    side: { ...draft.side, groups: newSideGroups },
    top: { ...draft.top, groups: newTopGroups },
  }
}

/**
 * Hides the ROW group labels — the nested side group columns that carry
 * filter/question labels — from the DISPLAY (#969 Obs 15, scoped to the row
 * side by #969 comment 2 item 4). The row labels themselves (the side vec
 * label column) stay; only the group nesting goes. Top groups shift left by
 * the removed header width so their absolute coordinates stay true.
 */
export function stripRowGroupLabels(data: PlatinumData): PlatinumData {
  const sideGroups = data.side?.groups || []
  if (sideGroups.length === 0) return data

  const removedCols = Math.max(...sideGroups.map(g => g.x ?? 0)) + 1
  const topGroups = (data.top?.groups || []).map(g => ({
    ...g,
    x: (g.x ?? 0) - removedCols,
  }))

  return {
    ...data,
    side: { ...data.side, groups: [] },
    top: { ...data.top, groups: topGroups },
  }
}

/**
 * Hides the COLUMN group labels — the top group header rows above the column
 * labels — from the DISPLAY (#969 comment 2 item 4). The column labels
 * themselves stay. Side groups shift UP by the removed header depth, the
 * mirror of what `stripRowGroupLabels` does in column space.
 */
export function stripColumnGroupLabels(data: PlatinumData): PlatinumData {
  const topGroups = data.top?.groups || []
  if (topGroups.length === 0) return data

  const removedRows = Math.max(...topGroups.map(g => g.y ?? 0)) + 1
  const sideGroups = (data.side?.groups || []).map(g => ({
    ...g,
    y: (g.y ?? 0) - removedRows,
  }))

  return {
    ...data,
    top: { ...data.top, groups: [] },
    side: { ...data.side, groups: sideGroups },
  }
}

/** Whether each axis draws its group labels. */
export interface GroupLabelVisibility {
  rows: boolean
  columns: boolean
}

/**
 * Resolves the two scoped group-label toggles (#969 comment 2 item 4).
 *
 * The toggles default OFF for a widget the user configures from now on — the
 * default is written into `view_config` when the widget is first configured
 * (`withDefaultGroupLabelVisibility`), NOT inferred here. That split is
 * deliberate: absent flags mean a widget saved before the toggles existed, and
 * such a widget must keep the appearance it was signed off with. Defaulting to
 * OFF here would silently strip the group labels off every stored table on its
 * next render.
 *
 * The legacy single `hide_structural_labels` only ever cleared SIDE groups, so
 * it resolves to the row side alone; an explicit per-side flag wins over it.
 */
export function resolveGroupLabelVisibility(
  config: TableViewConfig | undefined,
): GroupLabelVisibility {
  const legacyRowsHidden = config?.hide_structural_labels === true
  return {
    rows: config?.show_row_group_labels ?? !legacyRowsHidden,
    columns: config?.show_column_group_labels ?? true,
  }
}

/**
 * Stamps the "both toggles OFF" default onto a freshly configured widget's
 * view config (#969 comment 2 item 4). Applied at the same seam as the
 * data-only selection default, so it reaches new widgets only. A side the user
 * has already decided is left alone.
 */
export function withDefaultGroupLabelVisibility(
  config: TableViewConfig | undefined,
): TableViewConfig {
  return {
    ...config,
    show_row_group_labels: config?.show_row_group_labels ?? false,
    show_column_group_labels: config?.show_column_group_labels ?? false,
  }
}
